import { produce } from 'immer';
import { extractError } from 'app/lib/error';
import { REHYDRATE } from 'app/store/types.js';
import { UPDATE_ENTITY_DESCRIPTION } from 'app/store/entities/types.js';
import {
    GET_STARTERS,
    START_CONVERSATION,
    SEND_MESSAGE,
    DELETE_MESSAGE,
    SELECT_CONVERSATION,
    NEW_CONVERSATION,
    OPEN_NOTEBOOK,
    DELETE_CONVERSATION,
    GET_CONFIG,
    SET_CONFIG,
    CREATE_SQL_VIEW,
    DOCUMENT_NODE,
    DESCRIBE_NODE_SQL,
    SAVE_QUERY,
    UPDATE_SQL
} from './types.js';
import { Role, parseBlocks, parseStarters, hasError, findMessageBlock } from './utils.js';

export const INITIAL_STATE = {
    isThinking: false,
    error: null,
    isGettingStarters: false,
    starters: [],
    conversations: {},
    currentConversationIds: {},
    config: null,
    isSavingByUuid: {},
    isDocumentingNode: false
};

export const notebookReducer = produce((draft, action) => {
    switch (action.type) {
        case REHYDRATE:
            if ('notebook' in action.payload) {
                const { conversations } = action.payload.notebook;
                if (conversations) {
                    draft.conversations = conversations;
                }
            }
            break;
        case OPEN_NOTEBOOK:
            draft.currentConversationIds = {};
            draft.error = null;
            draft.starters = [];
            break;
        case GET_STARTERS.STARTED:
            draft.isGettingStarters = true;
            draft.error = null;
            break;
        case GET_STARTERS.SUCCEEDED: {
            const response = action.payload.responseJson;

            if (hasError(response)) {
                draft.error = response.error.message;
            } else {
                try {
                    draft.starters = parseStarters(response);
                } catch (e) {
                    draft.error = `Error parsing response from GPT: ${e}`;
                }
            }

            break;
        }
        case GET_STARTERS.FAILED:
            draft.error = extractError(action.payload);
            break;
        case GET_STARTERS.ENDED:
            draft.isGettingStarters = false;
            break;
        case START_CONVERSATION: {
            const { viewUuid, uuid, messages } = action.payload;
            draft.conversations[viewUuid] ||= {};
            draft.conversations[viewUuid][uuid] = {
                viewUuid,
                uuid,
                messages,
                startIndex: messages.length
            };
            draft.currentConversationIds = { viewUuid, uuid };
            draft.starters = [];
            break;
        }
        case SEND_MESSAGE.STARTED: {
            const { viewUuid, uuid, message } = action.meta;
            draft.isThinking = true;
            draft.error = null;
            draft.conversations[viewUuid][uuid].messages.push({
                role: Role.USER,
                content: message
            });
            break;
        }
        case SEND_MESSAGE.SUCCEEDED: {
            const { viewUuid, uuid } = action.meta;
            const response = action.payload.responseJson;

            if (hasError(response)) {
                draft.error = response.error.message;
            } else {
                const blocks = parseBlocks(response);
                draft.conversations[viewUuid][uuid].messages.push({
                    ...response.choices[0].message,
                    blocks
                });
            }
            break;
        }
        case SEND_MESSAGE.FAILED:
            draft.error = extractError(action.payload);
            break;
        case SEND_MESSAGE.ENDED:
            draft.isThinking = false;
            break;
        case DELETE_MESSAGE: {
            const { viewUuid, uuid, index } = action.payload;
            draft.conversations[viewUuid][uuid].messages.splice(index, 1);
            break;
        }
        case SELECT_CONVERSATION: {
            const { viewUuid, uuid } = action.payload;
            draft.currentConversationIds = { viewUuid, uuid };
            break;
        }
        case NEW_CONVERSATION:
            draft.currentConversationIds = {};
            break;
        case DELETE_CONVERSATION: {
            const { viewUuid, uuid } = action.payload;
            delete draft.conversations[viewUuid][uuid];

            if (draft.currentConversationIds.uuid === uuid) {
                draft.currentConversationIds = {};
            }

            break;
        }
        case GET_CONFIG.SUCCEEDED:
            draft.config = action.payload;
            break;
        case SET_CONFIG.STARTED:
            draft.isConfiguring = true;
            draft.error = null;
            break;
        case SET_CONFIG.FAILED:
            draft.error = extractError(action.payload);
            break;
        case SET_CONFIG.ENDED:
            draft.isConfiguring = false;
            break;
        case SAVE_QUERY.STARTED: {
            const { uuid } = action.meta;
            draft.isSavingByUuid[uuid] = true;
            break;
        }
        case SAVE_QUERY.FAILED: {
            const { uuid } = action.meta;
            delete draft.isSavingByUuid[uuid];
            break;
        }
        case CREATE_SQL_VIEW.ENDED: {
            const { uuid } = action.meta;
            delete draft.isSavingByUuid[uuid];
            break;
        }
        case DOCUMENT_NODE.STARTED:
            draft.isDocumentingNode = true;
            break;
        case UPDATE_ENTITY_DESCRIPTION.ENDED:
            draft.isDocumentingNode = false;
            break;
        case DESCRIBE_NODE_SQL.FAILED:
            draft.isDocumentingNode = false;
            break;
        case UPDATE_SQL: {
            const { viewUuid, conversationUuid, blockUuid, sql } = action.payload;
            const conversation = draft.conversations[viewUuid][conversationUuid];
            const block = findMessageBlock(conversation, blockUuid);

            block.content = sql;

            break;
        }
        default:
    }
}, INITIAL_STATE);
